import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Clock } from "lucide-react";

// Sélecteur de durée (prestation à l'heure) — bornes et km/heure pilotés par l'admin
export default function DurationSelect({ value, onChange, t, minHours = 4, maxHours = 24, kmPerHour = 40 }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const HOURS = Array.from({ length: maxHours - minHours + 1 }, (_, i) => i + minHours);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative" data-testid="booking-duration-selector">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="booking-duration-trigger"
        className={`flex w-full items-center gap-3 rounded-2xl border bg-white px-4 py-3.5 text-left transition-[border-color,box-shadow] ${
          open ? "border-miles shadow-[0_0_0_4px_rgba(36,87,255,0.10)]" : "border-ink/10 hover:border-miles/35"
        }`}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-miles-ice text-miles">
          <Clock className="h-4.5 w-4.5" strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-medium uppercase tracking-[0.08em] text-slate">{t("bk.duration")}</span>
          <span className="mt-0.5 flex items-baseline gap-2">
            <span className="text-base font-semibold tracking-tight text-ink">{value} h</span>
            <span className="text-xs text-slate">{t("bk.duration_km_short").replace("{km}", value * kmPerHour)}</span>
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate transition-transform duration-200 ${open ? "rotate-180 text-miles" : ""}`}
          strokeWidth={2}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 z-30 mt-2 max-h-72 origin-top overflow-y-auto rounded-2xl border border-ink/10 bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(11,31,77,0.35)]"
            data-testid="booking-duration-options"
          >
            {HOURS.map((hours) => {
              const selected = hours === value;
              return (
                <li key={hours} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    data-testid={`booking-duration-${hours}`}
                    onClick={() => { onChange(hours); setOpen(false); }}
                    className={`flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-left transition-colors ${
                      selected ? "bg-miles-ice text-miles" : "text-ink hover:bg-cloud"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className={`text-sm font-semibold ${selected ? "text-miles" : "text-ink"}`}>{hours} h</span>
                      <span className="text-xs text-slate">{t("bk.duration_km_short").replace("{km}", hours * kmPerHour)}</span>
                    </span>
                    {selected && <Check className="h-4 w-4 text-miles" strokeWidth={2.5} />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
